import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import { IconPaths, ModalSize, showError } from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import { Button, PageHeading, useModal } from 'app/components'
import { getEmpresaById } from '../../../../empresa/empresas-list/core/_requests'
import { getUnidadeById } from '../../../../unidades/unidades-list/core/_requests'
import { isOrigemEmpresa, OrigemEndereco } from '../../core/_origem'
import { EnderecoModal } from '../modal/EnderecoModal'

type Props = {
  origem: OrigemEndereco
}

type Identificacao = {
  nome?: string
  ativa?: boolean
  detalhes: (string | undefined)[]
}

/**
 * Cabeçalho da tela de endereços, acima do <TableCard>: nome de quem é dono dos endereços
 * (instituição, entidade ou unidade) como título; tipo da origem, documento/código e situação
 * como apoio; "Novo endereço" como ação.
 */
export const EnderecosHeading = ({ origem }: Props) => {
  const { id } = useParams<{ id: string }>()
  const { showModal } = useModal()
  const { t } = useT()

  const rotuloOrigem =
    origem === 'instituicao'
      ? t('empresa.label')
      : origem === 'entidade'
      ? t('entidade.label')
      : t('unidade_setor.label')

  const { data, isError } = useQuery(
    ['enderecos-origem', origem, id],
    async (): Promise<Identificacao> => {
      if (isOrigemEmpresa(origem)) {
        const empresa = await getEmpresaById(Number(id))
        return {
          nome: empresa.nomeFantasia || empresa.nome,
          ativa: empresa.ativa,
          detalhes: [empresa.cnpj && `CNPJ ${empresa.cnpj}`],
        }
      }
      const unidade = await getUnidadeById(Number(id))
      return {
        nome: unidade.nome,
        ativa: unidade.ativa,
        detalhes: [
          unidade.codigo && `Código ${unidade.codigo}`,
          unidade.tipo?.nome,
          unidade.empresa?.nomeFantasia,
        ],
      }
    },
    { enabled: Boolean(id), refetchOnWindowFocus: false, onError: showError }
  )

  const titulo =
    data?.nome ?? (isError ? rotuloOrigem : `Carregando ${rotuloOrigem.toLowerCase()}…`)
  // Situação por texto (não só cor): só aparece quando a origem está inativa.
  const descricao = [rotuloOrigem, ...(data?.detalhes ?? []), data?.ativa === false && 'Inativa']
    .filter(Boolean)
    .join(' · ')

  const openCreateModal = () =>
    showModal({
      title: 'Cadastrar endereço',
      size: ModalSize.Large,
      content: <EnderecoModal origem={origem} idOrigem={Number(id)} />,
    })

  return (
    <PageHeading
      title={titulo}
      description={descricao}
      actions={
        <Button className='btn-primary' icon={IconPaths.Add} onClick={openCreateModal}>
          Novo endereço
        </Button>
      }
    />
  )
}
